<template>
  <div>
    <PageHeader
      left-side-class="col-xl-4"
      :page="page"
      right-side-class="col-xl-8 align-items-end d-flex pb-2 justify-content-end"
    >
      <template #customActions>
        <drop-down
          v-if="userHasPermissions('visitregistry.visitcertification.download')"
          class="visit-certification-dropdown"
          icon="fas fa-clipboard"
          :items="visitCertificationActionItems"
          title="Certificaciones de Visitas"
        />
      </template>
    </PageHeader>

    <FilterManager
      ref="visitRegistryFilterRef"
      v-model="selectedFilters"
      :filters="fullFiltersConfiguration"
      :open-advanced="true"
      @academicYear="onChangeAcademicYear"
      @action="onChangeActions"
      @age="onChangeAge"
      @center="onChangeCenter"
      @location="onChangeLocation"
      @locationsType="onChangeLocationsType"
      @month="onChangeMonth"
      @provider="onChangeProvider"
      @search="onSearchHandler"
      @service="onChangeService"
      @sie="onChangeSie"
      @specialist="onChangeSpecialist"
    />

    <CustomSeparator
      v-if="results != null && results.length > 0"
      class="pt-3 my-3"
      text="Resultado de Referido"
    />

    <div class="mt-4">
      <section v-for="(item, index) in results" :key="'visit-registry' + index">
        <CustomCallout
          :border-color="getBorderColor(item)"
          class="mb-3 custom-callout"
          :controlled="true"
          :opened="item.isOpen"
          @click:open="onToggleAdditionalInfo(item)"
        >
          <div class="row">
            <div class="col-10">
              <div class="row py-3">
                <div class="col-md-3 col-sm-12 mr-md-3">
                  <div class="color-gray-6 font-14 font-weight-normal">
                    Estudiante:
                  </div>
                  <div class="font-weight-normal text-black">
                    {{ item.studentSieId }} - {{ item.studentName }}
                  </div>
                </div>
                <div class="col-md-2 col-sm-12 ml-md-5">
                  <div class="color-gray-6 font-14 font-weight-normal">
                    Edad:
                  </div>
                  <div class="font-weight-normal text-black">
                    {{ item.studentAge }}
                  </div>
                </div>
                <div class="col-md-2 col-sm-12 mt-3 mt-md-0 mt-sm-3">
                  <div class="color-gray-6 font-14 font-weight-normal">
                    Servicio:
                  </div>
                  <div class="font-weight-normal text-black">
                    {{ item.disciplineName }}
                  </div>
                </div>
                <div class="col-md-2 col-sm-12 mt-3 mt-md-0 mt-sm-3">
                  <div class="color-gray-6 font-14 font-weight-normal">
                    Tipo de Lugar:
                  </div>
                  <div class="font-weight-normal text-black">
                    {{ item.locationTypeName }}
                  </div>
                </div>
                <div class="col-md-2 col-sm-12 mt-3 mt-md-0 mt-sm-3">
                  <div class="color-gray-6 font-14 font-weight-normal">
                    Lugar de Servicio:
                  </div>
                  <div class="font-weight-normal text-black">
                    {{ item.locationName }}
                  </div>
                </div>
              </div>
            </div>
            <div
              class="col-md-2 col-sm-12 col-spacing row align-items-start justify-content-end"
            >
              <ActionButton
                class="ml-4 px-4"
                i="fas fa-file-import"
                text="Entrar"
                variant="outline-success"
                @click="onEnterHandler(item)"
              />
            </div>
          </div>
          <template v-if="item.isOpen" #edition class="p-3 pb-2 p-3">
            <div class="bg-secondary-9 p-2">
              <div class="info-panel bg-white rounded">
                <div class="row">
                  <div class="col-md-8 col-sm-12">
                    <div class="info-panel-main-title">
                      <em class="fas fa-info-circle" />
                      <span>Información General</span>
                    </div>
                  </div>
                  <div class="col-md-3 col-sm-12">
                    <div class="info-panel-main-title">
                      <em class="fas fa-info-circle" />
                      <span>Visitas</span>
                    </div>
                  </div>
                </div>
                <educative-additional-info
                  v-if="item.isEducativeService"
                  :assignment-end-date="dateFormat(item.assignmentEndDate)"
                  :assignment-start-date="dateFormat(item.assignmentStartDate)"
                  custom-class="mt-2 ml-2"
                  :show-visit-details="true"
                  :value="item.educationServiceInfo || {}"
                  :visits-absence="item.visitsAbsence"
                  :visits-regular="item.visitsRegular"
                />
                <div v-else class="info-panel-container">
                  <div class="col-md-8 col-sm-12 mr-md-5">
                    <!-- <div class="info-panel-item col-md-4 col-sm-12">
                      <span>Edad:</span>
                      <label>{{ item.studentAge }}</label>
                    </div> -->
                    <div class="info-panel-item col-md-3 col-sm-12">
                      <span>Modalidad:</span>
                      <label>{{ item.modality }}</label>
                    </div>
                    <div class="info-panel-item col-md-3 col-sm-12">
                      <span>Frecuencia:</span>
                      <label>{{ item.frequency }}</label>
                    </div>
                    <div class="info-panel-item col-md-3 col-sm-12">
                      <span>Duración:</span>
                      <label>{{ item.duration }}</label>
                    </div>
                    <div class="info-panel-item col-md-3 col-sm-12">
                      <span>Vigencia:</span>
                      <label>
                        {{ item.effectiveDateFrom }} -
                        {{ item.effectiveDateTo }}
                      </label>
                    </div>
                  </div>
                  <div
                    class="col-md-3 col-sm-12 col-spacing d-flex align-items-start visit-day border-left pl-3 pl-md-3 pl-sm-3 mb-2 pt-0"
                  >
                    <div class="flex-grow-1 ml-1 ml-md-0 ml-sm-1">
                      <div>
                        <div class="color-gray-6 font-14 font-weight-normal">
                          Dí­as Registrados en Visitas Regulares:
                        </div>
                        <div class="d-flex row pl-3 pl-md-3 pl-sm-3">
                          <div
                            v-for="(visit, j) in item.visitsRegular"
                            :key="`visit-regular-key-${j}`"
                            class="bg-secondary-2 visit-day__box rounded col-1 mt-1 mr-1"
                          >
                            <span class="color-black font-14">
                              {{ visit.visitDay }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="mt-3">
                        <div class="color-gray-6 font-14 font-weight-normal">
                          Dí­as Registrados en Ausencia:
                        </div>
                        <div class="d-flex row pl-md-3 pl-sm-3">
                          <div
                            v-for="(visit, j) in item.visitsAbsence"
                            :key="`visit-regular-key-${j}`"
                            class="bg-visit-absence visit-day__box rounded mr-2 col-1 mt-1"
                          >
                            <span class="color-black font-14">
                              {{ visit.visitDay }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div v-if="!item.isEducativeService" class="mt-3">
                        <div class="color-gray-6 font-14 font-weight-normal">
                          Dí­as Registrados en Horas Compensatorias:
                        </div>
                        <div class="d-flex pl-md-3 pl-sm-3">
                          <div
                            v-for="(visit, j) in item.visitsCompensatory"
                            :key="`visit-regular-key-${j}`"
                            class="bg-visit-compensatory visit-day__box rounded mr-2"
                          >
                            <span class="color-black font-14">
                              {{ visit.visitDay }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="selectedFilters.supervisedSpecialist"
                  class="info-panel-container"
                >
                  <div class="info-panel-item">
                    <span>Especialista Supervisado:</span>
                    <label>
                      {{ selectedFilters.supervisedSpecialist.name }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </CustomCallout>

        <div
          v-if="showErrorContainer(item)"
          class="bg-secondary-19 p-2 rounded mt-n3 mb-3"
        >
          <div
            v-if="item.errors"
            class="color-secondary-21 font-14 font-weight-normal d-flex align-items-center"
          >
            <em class="fas fa-exclamation-circle pl-2 ml-1 color-primary-4" />
            <div class="ml-2 no-eligible-message pl-1 color-secondary-20">
              ({{ item.errors }}) Error
            </div>
          </div>
          <div
            v-if="isPeiNotSigned(item)"
            class="color-secondary-21 font-14 font-weight-normal d-flex align-items-center"
          >
            <em class="fas fa-file-invoice pl-2 ml-1 color-primary-4" />
            <div class="ml-2 no-eligible-message pl-2 color-secondary-20">
              No cuenta con un PEI del año corriente firmado en sistema.
            </div>
          </div>
        </div>
      </section>

      <custom-result-not-found v-if="results != null && results.length === 0" />
    </div>
    <Pagination
      v-if="results != null && results.length > 0"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import FilterManager from 'FilterManager';
import PageHeader from 'PageHeader';
import CustomSeparator from '@/components/common/CustomSeparator';
import ActionButton from 'ActionButton';
import DropDown from 'dropdown';
import CustomCallout from 'custom-callout';
import Pagination from '@/components/common/Pagination.vue';
import { name } from '@/modules/visitRegistry/store';
import {
  ACTION_TYPES,
  MUTATION_TYPES,
} from '@/modules/visitRegistry/store/types';
import { mapState as mapMainState, createNamespacedHelpers } from 'vuex';
const {
  mapActions,
  mapState,
  mapGetters,
  mapMutations,
} = createNamespacedHelpers(name);
import alertMixin from '@/mixins/alert.mixin.js';
import planStatus from '@/utils/constants/thrpyPlanStatus.js';
import customResultNotFound from 'custom-result-not-found';
import storeMixin from 'utils/mixins/storeGenerator';

import _visitRegistryApi from '@/modules/visitRegistry/api.js';
import _generalApi from '@/modules/app/api.js';
import roles from '@/utils/constants/roles.js';
import EducativeAdditionalInfo from '@/components/common/visits/EducativeAdditionalInfo';

const VISIT_VALIDATION_ROUTE_NAME = 'VisitRegistryValidate';

export default {
  name: 'ProviderVisitRegistry',
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    ActionButton,
    DropDown,
    CustomCallout,
    Pagination,
    customResultNotFound,
    EducativeAdditionalInfo,
  },
  mixins: [alertMixin, storeMixin],
  data: () => ({
    catalog: {
      ages: [
        { id: null, name: 'Todos' },
        { id: '3,5', name: '3 - 5' },
        { id: '6,21', name: '6 - 21' },
      ],
      actions: [
        { id: 0, name: 'Todos' },
        { id: 1, name: 'Correcto' },
        { id: 2, name: 'Errores' },
      ],
    },
    page: {
      title: 'Registro de Visitas',
      icon: 'fas fa-user-tie',
      routes: ['Proveedor', 'Registro de Visitas'],
    },
    planStatus: planStatus,
    isCertificationVisitRequester: false,
    selectedFilters: {},
  }),
  computed: {
    ...mapGetters({
      pagination: 'getPagination',
    }),
    ...mapState({
      academicYears: (state) => state.catalog.academicYears,
      months: (state) => state.catalog.months,
      monthsOriginal: (state) => state.catalog.monthsOriginal,
      services: (state) => state.catalog.services,
      centers: (state) => state.catalog.centers,
      locationsType: (state) => state.catalog.locationsType,
      locations: (state) => state.catalog.locations,
      results: (state) => state.filter.results,
      specialists: (state) => state.catalog.specialists,
      providers: (state) => state.catalog.providers,
      supervisedSpecialists: (state) => state.catalog.supervisedSpecialists,
    }),
    getUserRelatedCompanies() {
      return this.$store.state['$_user'].companies;
    },
    ...mapMainState('$_user', ['user']),
    filterManagerConfig() {
      const filtersConfig = {
        basic: [
          {
            name: 'academicYear',
            component: 'CustomDropDown',
            options: this.academicYears,
            initialValue: this.selectedFilters.academicYear,
            placeholder: 'Seleccione el año',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            class: 'col-md-2',
          },
          {
            name: 'month',
            component: 'CustomDropDown',
            options: this.months,
            initialValue: this.selectedFilters.month,
            placeholder: 'Seleccione el mes',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            class: 'col-md-2',
            validations: 'required',
            error: 'Favor seleccionar el mes.',
          },
          {
            name: 'provider',
            component: 'CustomDropDown',
            options: this.providers,
            disabled: this.providers && this.providers.length == 1,
            initialValue: this.selectedFilters.provider,
            placeholder: 'Seleccione Proveedor',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            class: 'col-md-4',
            validations: 'required',
            error: 'El proveedor es requerido.',
          },
          {
            name: 'specialist',
            component: 'CustomDropDown',
            options: this.specialists,
            disabled: this.isSpecialist,
            initialValue: this.selectedFilters.specialist,
            placeholder: 'Seleccione Especialista',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            class: 'col-md-4',
            validations: 'required',
            error: 'El especialista es requerido.',
          },
        ],
        advanced: [
          {
            name: 'center',
            component: 'CustomDropDown',
            options: this.centers,
            initialValue: this.selectedFilters.center,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-3',
            label: 'Centro:',
            isAdvanced: true,
            validations: 'required',
            error: 'Favor seleccionar el centro.',
          },
          {
            name: 'service',
            component: 'CustomDropDown',
            options: this.services,
            initialValue: this.selectedFilters.service,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-3',
            label: 'Servicio:',
            isAdvanced: true,
            validations: 'required',
            error: 'Favor seleccionar el servicio.',
          },
          {
            name: 'locationsType',
            component: 'CustomDropDown',
            options: this.locationsType,
            initialValue: this.selectedFilters.locationType,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-3',
            label: 'Tipo de Lugar:',
            isAdvanced: true,
          },
          {
            name: 'location',
            component: 'CustomDropDown',
            options: this.locations,
            initialValue: this.selectedFilters.location,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-3',
            label: 'Lugar:',
            isAdvanced: true,
          },
          {
            name: 'age',
            component: 'CustomDropDown',
            options: this.catalog.ages,
            initialValue: this.selectedFilters.age,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-3',
            label: 'Edad:',
            isAdvanced: true,
          },
          {
            name: 'action',
            component: 'CustomDropDown',
            options: this.catalog.actions,
            initialValue: this.selectedFilters.action,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-3',
            label: 'Acción:',
            isAdvanced: true,
          },
          {
            name: 'sie',
            component: 'CustomInput',
            class: 'col-md-3',
            label: 'SIE:',
            placeholder: 'Escriba número de SIE',
            initialValue: this.selectedFilters.sie,
            isAdvanced: true,
          },
          {
            name: 'supervisedSpecialist',
            component: 'CustomDropDown',
            options: this.supervisedSpecialists,
            initialValue: this.selectedFilters.supervisedSpecialist,
            label: 'Especialista Supervisado:',
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-3',
            isAdvanced: true,
            visible: this.isSpecialist,
          },
        ],
      };
      return filtersConfig;
    },
    getBorderColor() {
      return (item) => {
        return item.error ? 'pink-error' : '';
      };
    },
    toggleClass() {
      return (isOpen) => ({
        'far fa-chevron-circle-right orange-color': !isOpen,
        'far fa-chevron-circle-down green-color': isOpen,
      });
    },
    fullFiltersConfiguration() {
      return this.filterManagerConfig.basic.concat(
        this.filterManagerConfig.advanced
      );
    },
    showErrorContainer() {
      return (item) =>
        item.errors || (item.isEducativeService && !item.isPeiSigned);
    },
    isAdmin() {
      return [
        roles.AdminProvider,
        roles.AdminProInfo,
        roles.Administrador,
        roles.ProviderCPA,
      ].includes(this.userRole);
    },
    haveMoreThanOneComp() {
      return this.providers?.length > 1;
    },
    isProvider() {
      return [roles.Provider, roles.AsistenteProveedor].includes(this.userRole);
    },
    isSpecialist() {
      return [roles.Especialista].includes(this.userRole);
    },
    isSIE() {
      return (value) => value?.trim().length <= 8 ?? null;
    },
    isPeiNotSigned() {
      return (item) => item.isEducativeService && !item.isPeiSigned;
    },
    visitCertificationActionItems() {
      return [
        {
          label: 'Manuscrito',
          click: () => this.processVisitCertification(false),
        },
        {
          label: 'Electrónico',
          click: () => this.processVisitCertification(true),
        },
      ];
    },
  },
  watch: {
    academicYears() {
      if (
        this.selectedFilters.academicYear == null &&
        this.academicYears.length > 0
      ) {
        this.selectedFilters.academicYear = this.currentAcademicYear();
        this[ACTION_TYPES.GET_MONTHS](this.selectedFilters.academicYear);
        if (this.isProvider) {
          this.validateSingleFilter();
        }
      }
    },
  },
  mounted() {
    this[ACTION_TYPES.GET_ACADEMIC_YEARS]();
    this[ACTION_TYPES.GET_LOCATIONSTYPE]();
    this[ACTION_TYPES.SET_FILTERED_PERIOD](this.selectedFilters.month);
    this.selectedFilters.isAdminOrSpecialist =
      this.isAdmin || this.isSpecialist;
    if (this.isProvider) {
      this.validateSingleFilter();
    }
    if (this.results?.length > 0) {
      if (this.pagination?.currentPage > 1) {
        this.goToPageHandler(this.pagination.currentPage);
      } else {
        this.doSearch();
      }
    }
  },
  destroyed() {
    this.persistData();
  },

  methods: {
    ...mapMutations([
      MUTATION_TYPES.SET_PROVIDERS,
      MUTATION_TYPES.SET_SPECIALIST,
      MUTATION_TYPES.SET_VISIT_FILTER,
    ]),
    ...mapActions([
      ACTION_TYPES.GET_ACADEMIC_YEARS,
      ACTION_TYPES.GET_MONTHS,
      ACTION_TYPES.SEARCH,
      ACTION_TYPES.GET_ADDITIONAL_INFORMATION,
      ACTION_TYPES.GET_PROVIDERS,
      //Advances Filters
      ACTION_TYPES.GET_SERVICES,
      ACTION_TYPES.GET_CENTERS,
      ACTION_TYPES.GET_LOCATIONSTYPE,
      ACTION_TYPES.GET_LOCATION,
      ACTION_TYPES.RESET_ADVANCED_FILTERS,
      ACTION_TYPES.GET_SPECIALIST,
      ACTION_TYPES.GET_SUPERVISED_SPECIALISTS,
      ACTION_TYPES.SET_FILTERED_PERIOD,
      ACTION_TYPES.SET_CLEAR_CATALOG,
    ]),
    async validateSingleFilter() {
      await this.setIsActualProvider();
      if (this.isSpecialist) {
        this.setIsActualSpecialist();
      }
    },
    async setIsActualProvider() {
      let companies = this.getUserRelatedCompanies;
      if (companies && companies.length == 1) {
        this.selectedFilters.provider = companies[0];
        this[MUTATION_TYPES.SET_PROVIDERS]([companies]);
      } else {
        await this[ACTION_TYPES.GET_PROVIDERS]({
          payload: {
            academicYearId: this.selectedFilters.academicYear.id,
            month: this.selectedFilters?.month?.id,
            specialistId: this.selectedFilters.specialist?.id,
          },
        });
        if (this.providers.length == 1) {
          this.selectedFilters.provider = this.providers[0];
        }
      }
    },
    setIsActualSpecialist() {
      let specialistInfo = this.$store.state['$_user'].user.profile;

      let specialist = {
        id: specialistInfo.specialistId,
        name: specialistInfo.givenname,
        sortOrder: 1,
        selected: true,
        isDirty: false,
      };

      this[MUTATION_TYPES.SET_SPECIALIST]([specialist]);
      this.selectedFilters.specialist = specialist;
    },
    async onChangeAcademicYear(value) {
      this.selectedFilters.academicYearId = value;
      this[ACTION_TYPES.GET_MONTHS](value);
      //Clear
      this.setClearCatalogValue({
        specialists: [],
        providers: [],
      });
      this.selectedFilters.specialist = null;
      this.selectedFilters.month = null;
      this.selectedFilters.center = null;
      this.selectedFilters.service = null;
      this.selectedFilters.provider = null;
      if (this.isProvider) {
        this.validateSingleFilter();
      }
      this.resetAdvanced();
      await this.$refs.visitRegistryFilterRef.$refs.formValidator.reset();
    },
    async onChangeMonth(value) {
      this.selectedFilters.month = value;
      this.selectedFilters.specialist = null;
      this.selectedFilters.center = null;
      this.selectedFilters.service = null;
      this.selectedFilters.provider = null;
      //Clear
      this.setClearCatalogValue({
        centers: [],
        services: [],
        specialists: [],
      });
      this[ACTION_TYPES.SET_FILTERED_PERIOD](
        this.monthsOriginal.find((x) => x.monthDesc === value.id)
      );
      await this.validateSingleFilter();
      let companies = this.getUserRelatedCompanies;
      if (this.isProvider || (companies && companies.length == 1)) {
        await this[ACTION_TYPES.GET_SPECIALIST]({
          academicYearId: this.selectedFilters.academicYear.id,
          month: value.id,
          companyId: this.selectedFilters?.provider?.id,
          disciplineTypeId: this.selectedFilters?.service?.id,
          isAdminOrSpecialist: this.selectedFilters.isAdminOrSpecialist,
        });

        const canLoadCenter =
          this.selectedFilters.specialist?.id > 0 &&
          this.selectedFilters.provider?.id > 0;
        // load center if already have company selected and specialist selected
        if (canLoadCenter && this.isSpecialist) this.loadCenters();
        //Validate provider cuz in this point it can be null and we dont need to call center
      } else if (this.isSpecialist && this.selectedFilters.provider) {
        this.loadCenters();
      }
      this.resetAdvanced();
    },
    async onChangeProvider(value) {
      this.selectedFilters.provider = value;
      this.selectedFilters.center = null;
      this.selectedFilters.service = null;
      this.selectedFilters.specialist = null;
      this.selectedFilters.supervisedSpecialist = null;
      //Clear
      this.setClearCatalogValue({
        centers: [],
        specialists: [],
      });

      if (this.isSpecialist) {
        this.setIsActualSpecialist();
        this.loadCenters();
        this[ACTION_TYPES.GET_SUPERVISED_SPECIALISTS]({
          academicYearId: this.selectedFilters?.academicYear?.id,
          companyId: this.selectedFilters?.provider?.id,
          specialistId: this.selectedFilters?.specialist?.id,
        });
      } else {
        this[ACTION_TYPES.GET_SPECIALIST]({
          academicYearId: this.selectedFilters.academicYear.id,
          month: this.selectedFilters?.month?.id,
          companyId: this.selectedFilters?.provider?.id,
          disciplineTypeId: this.selectedFilters?.service?.id,
          isAdminOrSpecialist: this.selectedFilters.isAdminOrSpecialist,
        });
      }
    },
    onChangeSpecialist(value) {
      this.selectedFilters.specialist = value;
      this.selectedFilters.center = null;
      this.selectedFilters.service = null;

      this.loadCenters();
    },

    //On change Advances FIlters
    onChangeLocationsType(value) {
      this.selectedFilters.locationType = value;
      this.selectedFilters.location = null;

      this[ACTION_TYPES.GET_LOCATION]({
        month: this.selectedFilters.month.id,
        isAdminOrSpecialist: this.selectedFilters.isAdminOrSpecialist,
        specialistId: this.selectedFilters.specialist.id,
        companyId: this.selectedFilters.provider?.id,
        locationTypeId: value.id,
      });
    },
    onChangeService(value) {
      this.selectedFilters.service = value;
    },
    onChangeCenter(value) {
      this.selectedFilters.center = value;
      this.selectedFilters.service = null;
      this[ACTION_TYPES.GET_SERVICES]({
        month: this.selectedFilters.month.id,
        specialistId: this.selectedFilters.specialist?.id,
        companyId: this.selectedFilters.provider?.id,
      });
    },
    onChangeLocation(value) {
      this.selectedFilters.location = value;
    },
    onChangeAge(value) {
      this.selectedFilters.age = value;
    },
    onChangeActions(value) {
      this.selectedFilters.action = value;
    },
    onChangeSie(value) {
      this.selectedFilters.sie = value;
    },
    loadCenters() {
      this[ACTION_TYPES.GET_CENTERS]({
        academicYearId: this.selectedFilters.academicYear?.id,
        month: this.selectedFilters.month?.id,
        isAdminOrSpecialist: this.selectedFilters.isAdminOrSpecialist,
        specialistId: this.selectedFilters.specialist?.id,
        companyId: this.selectedFilters.provider?.id,
      });
    },
    async processVisitCertification(isForDigitalSignature = false) {
      let model = {};

      this.isCertificationVisitRequester = true;
      await this.$refs.visitRegistryFilterRef.$refs.formValidator.reset();

      const isValid = await this.$refs.visitRegistryFilterRef.$refs.formValidator.validate();

      if (
        isValid &&
        this.selectedFilters.service != null &&
        this.selectedFilters.center != null
      ) {
        model.academicYearId = this.selectedFilters.academicYear.id;
        model.year = this.selectedFilters.month.year;
        model.month = this.selectedFilters.month.monthNumber;
        model.specialistId = !this.selectedFilters.supervisedSpecialist?.id
          ? this.selectedFilters.specialist?.id
          : this.selectedFilters.supervisedSpecialist?.id;
        model.companyId = this.selectedFilters.provider?.id;
        model.disciplineTypeId = this.selectedFilters.service.id;
        model.centerId = this.selectedFilters.center.id;
        model.locationTypeId = this.selectedFilters.locationType
          ? this.selectedFilters.locationType.id
          : null;
        model.locationFiscalYearId = this.selectedFilters.location
          ? this.selectedFilters.location.id
          : null;
        model.studentAge = this.selectedFilters.age
          ? this.selectedFilters.age.id
          : null;
        model.isForDigitalSignature = isForDigitalSignature;

        if (this.selectedFilters.sie != null)
          model.studentSieId = parseInt(this.selectedFilters.sie);

        await _visitRegistryApi
          .getVisitsCertification(model)
          .then((response) => {
            _generalApi.ConvertToBlob(response.data);
          })
          .catch((err) => {
            this.ShowToast('Error', err.response.data.message, 'error');
          });
      } else {
        this.ShowToast(
          'Error',
          'Favor seleccionar el año, mes, especialista, servicio y CSEE de Facturación  para procesar la certificación de visita.',
          'error'
        );
        this.$refs.visitRegistryFilterRef.advanced = true;

        //to show error validation in advanced filters needs to reset and validate again.
        await this.$refs.visitRegistryFilterRef.$refs.formValidator.reset();
        await this.$refs.visitRegistryFilterRef.$refs.formValidator.validate();
      }
    },
    async onEnterHandler(item) {
      //list of periods to disabled
      if (!item.canEnterVisit) {
        const confirmation = await this.ShowCancelModal(
          'Confirmación',
          'Para poder continuar con el proceso de registro de visita, es necesario que el estudiante tenga un espacio coordinado en el calendario de disponibilidad del especialista. ¿Desea Coordinar el espacio?',
          'Si',
          'No'
        );
        if (!confirmation) return;
        this.$router.push({
          name: 'SpecialistAvailabilityCalendar',
          params: { admissionId: item.admissionId },
        });
        return;
      }

      if (item.hasApprovedExemption != null && !item.hasApprovedExemption) {
        this.ShowToast(
          'Error',
          'No se puede registrar la visita. El especialista no tiene una dispensa aprobada por el DEPR. Por favor, asegúrese de que la dispensa esté aprobada antes de intentar registrar una nueva visita.',
          'error'
        );
        return;
      }

      if (!item.hasPlan) {
        this.ShowToast(
          'Error',
          'No cuenta con un plan de intervención en sistema.',
          'error'
        );
        return;
      }

      const disabledPeriods = this.months
        .slice(
          _.findIndex(this.months, { id: this.selectedFilters.month.id }) + 1
        )
        .map(({ monthNumber, year }) =>
          moment()
            .date(1)
            .month(monthNumber - 1)
            .year(year)
            .toDate()
        );

      this.$router.push({
        name: VISIT_VALIDATION_ROUTE_NAME,
        params: {
          ...item,
          initialPeriod: this.months[0],
          disabledPeriods,
          filter: this.selectedFilters,
          readonly: this.selectedFilters.supervisedSpecialist?.id > 0,
        },
      });
    },
    onToggleAdditionalInfo(item) {
      item.serviceDate = this.selectedFilters.month?.id;
      this[ACTION_TYPES.GET_ADDITIONAL_INFORMATION](item);
    },
    async onSearchHandler() {
      if (!this.isSpecialist && !this.selectedFilters.specialist) {
        this.$refs.visitRegistryFilterRef.advanced = true;
        await this.$forceUpdate();
      }
      const isValid = await this.$refs.visitRegistryFilterRef.$refs.formValidator.validate();
      if (isValid) {
        //Update Last filters searching
        this.doSearch();
      } else {
        this.ShowToast('Error', 'Información Incompleta', 'error');
      }
    },
    doSearch() {
      this[MUTATION_TYPES.SET_VISIT_FILTER](this.selectedFilters);
      this[ACTION_TYPES.SEARCH]({
        academicYearId: this.selectedFilters.academicYear.id,
        serviceDate: this.selectedFilters.month?.id,
        companyId: this.selectedFilters.provider?.id,
        specialistId: !this.selectedFilters.supervisedSpecialist?.id
          ? this.selectedFilters.specialist?.id
          : this.selectedFilters.supervisedSpecialist?.id,
        disciplineTypeId: this.selectedFilters.service?.id,
        centerId: !this.selectedFilters.supervisedSpecialist?.id
          ? this.selectedFilters.center?.id
          : null,
        locationTypeId: this.selectedFilters.locationType?.id,
        locationFiscalYearId: this.selectedFilters.location?.id,
        studentAge: this.selectedFilters.age
          ? this.selectedFilters.age.id
          : null,
        action: this.selectedFilters.action
          ? this.selectedFilters.action.id
          : null,
        studentSieId: this.isSIE(this.selectedFilters.sie)
          ? this.selectedFilters?.sie
          : null,
        referralNumber: !this.isSIE(this.selectedFilters.sie)
          ? this.selectedFilters?.sie
          : null,
        pageIndex: 1,
        pageSize: 15, //Think
      });
    },
    goToPageHandler(page) {
      this[MUTATION_TYPES.SET_VISIT_FILTER](this.selectedFilters);
      this[ACTION_TYPES.SEARCH]({
        academicYearId: this.selectedFilters.academicYear.id,
        serviceDate: this.selectedFilters.month
          ? this.selectedFilters.month.id
          : null,
        companyId: this.selectedFilters.provider?.id,
        specialistId: !this.selectedFilters.supervisedSpecialist?.id
          ? this.selectedFilters.specialist?.id
          : this.selectedFilters.supervisedSpecialist?.id,
        //Advanced Filters
        disciplineTypeId: this.selectedFilters.service
          ? this.selectedFilters.service.id
          : null,
        centerId: !this.selectedFilters.supervisedSpecialist?.id
          ? this.selectedFilters.center?.id
          : null,
        locationTypeId: this.selectedFilters.locationType
          ? this.selectedFilters.locationType.id
          : null,
        locationFiscalYearId: this.selectedFilters.location
          ? this.selectedFilters.location.id
          : null,
        StudentAge: this.selectedFilters.age
          ? this.selectedFilters.age.id
          : null,
        Action: this.selectedFilters.action
          ? this.selectedFilters.action.id
          : null,
        StudentSieId: this.selectedFilters.sie
          ? this.selectedFilters.sie
          : null,
        pageIndex: page,
        pageSize: 15, //Think
      });
    },
    resetAdvanced() {
      this.selectedFilters.center = null;
      this.selectedFilters.service = null;
      this.selectedFilters.locationType = null;
      this.selectedFilters.location = null;
      this.selectedFilters.age = null;
      this.selectedFilters.action = null;
      this.selectedFilters.sie = null;
      this.selectedFilters.supervisedSpecialist = null;

      //SET EMPTY SERVICES
      this[ACTION_TYPES.RESET_ADVANCED_FILTERS]();
    },
    setClearCatalogValue(payload) {
      this[ACTION_TYPES.SET_CLEAR_CATALOG](payload);
    },
    dateFormat: function(date) {
      if (date) {
        return moment(date)
          .locale('es-PR')
          .format('DD/MMM/YYYY');
      }
      return '';
    },
  },
};
</script>

<style scoped lang="scss">
$collapseOpenColor: #bcbec0;
$collapseCloseColor: #ff7058;
$colorWhite: #fff;
$panelBorderColor: #f2f2f2;
$errorColor: #c5525c;
$visitAbsenceColor: #ffedbb;
$visitCompensatoryColor: #ff7058;

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 86px;
  .toggle-icon {
    flex: 1;
    text-align: center;
    cursor: pointer;
    em {
      margin-top: 12px;
      font-size: 26px;
    }
  }
  .separator-line {
    background: transparent linear-gradient(270deg, #f7f7f700 0%, #efefef 100%)
      0% 0% no-repeat padding-box;
    opacity: 1;
    width: 10px;
    height: 100%;
  }
}

.orange-color {
  color: #ff7058;
}

.green-color {
  color: #2db782;
}

.text-black {
  color: #11291b;
}

.info-panel {
  padding: 15px 10px 1px 20px;
  .info-panel-main-title {
    em {
      color: $collapseCloseColor;
    }
    span {
      text-align: left;
      font: Bold 16px/19px Lato;
      letter-spacing: 0px;
      color: #3f5b60;
      opacity: 1;
      margin-left: 7px;
    }
  }
  .info-panel-container {
    display: flex;
    flex-flow: row wrap;
    padding: 15px 10px 1px 23px;
    .info-panel-item {
      flex: 1 0 20%;
      margin-bottom: 0.5em;
      float: left;
      box-sizing: border-box;
      padding-right: 10px;
      span {
        display: block;
        text-align: left;
        font: Regular 14px/28px Lato;
        letter-spacing: 0px;
        color: #727272;
      }
      label {
        text-align: left;
        font: Regular Lato;
        font-size: 1rem;
        letter-spacing: 0px;
        color: #11291b;
        line-height: 1.6em;
      }
    }
  }
}

.action-button {
  margin-left: 114px;
}

.visit-certification-dropdown {
  ::v-deep .btn.dropdown-toggle {
    background-color: #28a745;
    border-color: #28a745;
    color: #fff;
  }
}
.student-spacing {
  margin-right: -60px;
}
.service-place-spacing {
  margin-right: -50px;
}

.error-indicator {
  margin-left: 10px;
  em {
    color: $errorColor;
  }
  span {
    font-size: 14px;
    color: $errorColor;
    margin-left: 5px;
  }
}

/* overrides */
.custom-callout {
  .col-spacing {
    padding: 20px 15px;
  }
  ::v-deep .callout-container {
    padding: 0px !important;
    border-left-width: 4px;
  }
  ::v-deep .callout-edition {
    margin-top: 3px !important;
    padding: 7px !important;
  }
}

.widgets_div .text_div {
  display: inline-block;
  width: calc(100% - 60px);
  vertical-align: top;
  font-size: 14px;
  color: $errorColor;
}
.widgets_div .icon_div {
  display: inline-block;
  vertical-align: top;
}

.no-info {
  background: #f7f7f7 0% 0% no-repeat padding-box;
  border-radius: 5px;
  opacity: 1;
  border-left-style: solid;
  border-color: #e8e8e8;
  border-width: 0;
  .no-info-text {
    text-align: left;
    font: Bold 16px/22px Lato;
    letter-spacing: 0;
    color: #727272;
    opacity: 1;
  }
  .no-info-icon {
    color: #727272;
    font-size: 32px;
  }
}

.visit-day {
  &__box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.688rem;
    height: 1.688rem;
  }
}

.bg-visit-absence {
  background: $visitAbsenceColor;
}

.bg-visit-compensatory {
  background: $visitCompensatoryColor;
}

@media only screen and (max-width: 768px) {
  .action-button {
    margin-left: 0px;
  }
  .visit-day {
    border-left: unset !important;
  }
}
</style>
